import { useState, useEffect } from "react";
import axios from "axios";
import {
  TbCalendarCheck, TbRefresh, TbSearch, TbFilter,
  TbAlertCircle, TbDoorEnter, TbDoorExit, TbCurrencyEuro,
  TbUser, TbBuilding, TbChevronDown, TbChevronUp
} from "react-icons/tb";

const API = "https://api.komorotel.com/api/agency";
const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => Math.round(eur * KMF_PER_EUR).toLocaleString("fr-FR");

const STATUS_CONFIG = {
  confirmed:      { label: "Confirmée",    bg: "bg-emerald-50",  text: "text-emerald-700", dot: "bg-emerald-400" },
  pending:        { label: "En attente",   bg: "bg-blue-50",     text: "text-blue-700",    dot: "bg-blue-400"    },
  pending_cancel: { label: "Annul. en att.", bg: "bg-orange-50", text: "text-orange-700",  dot: "bg-orange-400"  },
  cancelled:      { label: "Annulée",      bg: "bg-red-50",      text: "text-red-700",     dot: "bg-red-400"     },
  completed:      { label: "Terminée",     bg: "bg-gray-100",    text: "text-gray-600",    dot: "bg-gray-400"    },
  no_show:        { label: "No-show",      bg: "bg-gray-100",    text: "text-gray-500",    dot: "bg-gray-300"    },
};

const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
const fmtShort = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
const nightsBetween = (a, b) => Math.ceil((new Date(b) - new Date(a)) / 86400000);

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.confirmed;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

function ReservationRow({ r, expanded, onToggle }) {
  const nights = nightsBetween(r.checkInDate, r.checkOutDate);
  return (
    <div className="border-b border-gray-50 last:border-0">
      {/* Row principale */}
      <div
        className="flex items-center gap-4 py-3 px-4 hover:bg-gray-50 cursor-pointer transition"
        onClick={onToggle}
      >
        {/* Guest */}
        <div className="flex items-center gap-2 w-44 shrink-0">
          <div className="w-8 h-8 rounded-full bg-[#eaf3de] text-[#3B6D11] flex items-center justify-center text-xs font-medium shrink-0">
            {r.guest?.firstName?.[0]}{r.guest?.lastName?.[0]}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {r.guest?.firstName} {r.guest?.lastName}
            </p>
            <p className="text-xs text-gray-400 truncate">{r.guest?.email}</p>
          </div>
        </div>

        {/* Bien */}
        <div className="flex items-center gap-1.5 w-36 shrink-0">
          <TbBuilding size={13} className="text-gray-400 shrink-0" />
          <p className="text-xs text-gray-600 truncate">{r.hotelId?.name || "—"}</p>
        </div>

        {/* Dates */}
        <div className="flex items-center gap-1.5 w-36 shrink-0">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <TbDoorEnter size={13} className="text-emerald-500" />
            {fmtShort(r.checkInDate)}
          </div>
          <span className="text-gray-300">→</span>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <TbDoorExit size={13} className="text-blue-500" />
            {fmtShort(r.checkOutDate)}
          </div>
          <span className="text-xs text-gray-400 ml-1">· {nights}n</span>
        </div>

        {/* Prix */}
        <div className="w-28 shrink-0">
          <p className="text-sm font-bold text-gray-900">{r.totalPrice?.toLocaleString("fr-FR")} €</p>
          <p className="text-xs text-gray-400">{toKMF(r.totalPrice)} KMF</p>
        </div>

        {/* Statut */}
        <div className="flex-1 flex items-center justify-between">
          <StatusBadge status={r.status} />
          {expanded
            ? <TbChevronUp size={15} className="text-gray-400" />
            : <TbChevronDown size={15} className="text-gray-400" />
          }
        </div>
      </div>

      {/* Détail expandable */}
      {expanded && (
        <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3">
            {[
              { label: "ID réservation",  value: r.reservationId || r._id?.slice(-8).toUpperCase() },
              { label: "Chambre",         value: r.roomId?.title || "—" },
              { label: "Plan tarifaire",  value: r.ratePlanSnapshot?.name || "—" },
              { label: "Repas",           value: r.ratePlanSnapshot?.mealPlan || "—" },
              { label: "Check-in",        value: fmtDate(r.checkInDate) },
              { label: "Check-out",       value: fmtDate(r.checkOutDate) },
              { label: "Prix / nuit",     value: `${r.pricePerNight?.toLocaleString("fr-FR")} €` },
              { label: "Source",          value: r.source === "site" ? "Site web" : "Back-office" },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-3 border border-gray-100">
                <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                <p className="text-xs font-semibold text-gray-800">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AgencyReservations() {
  const [reservations, setReservations] = useState([]);
  const [hotels, setHotels]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [expanded, setExpanded]         = useState(null);

  const [filters, setFilters] = useState({
    search:  "",
    status:  "all",
    hotelId: "all",
    from:    "",
    to:      "",
  });

  const fetchData = () => {
    setLoading(true);
    setError(null);
    const params = {};
    if (filters.status  !== "all") params.status  = filters.status;
    if (filters.hotelId !== "all") params.hotelId = filters.hotelId;
    if (filters.from)               params.from    = filters.from;
    if (filters.to)                 params.to      = filters.to;

    Promise.all([
      axios.get(`${API}/reservations`, { params, withCredentials: true }),
      axios.get(`${API}`,              { withCredentials: true }),
    ])
      .then(([resRes, hotelsRes]) => {
        setReservations(resRes.data);
        setHotels(hotelsRes.data);
      })
      .catch(() => setError("Impossible de charger les réservations."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [filters.status, filters.hotelId, filters.from, filters.to]);

  const filtered = reservations.filter(r => {
    if (!filters.search) return true;
    const s = filters.search.toLowerCase();
    return (
      r.guest?.firstName?.toLowerCase().includes(s) ||
      r.guest?.lastName?.toLowerCase().includes(s)  ||
      r.guest?.email?.toLowerCase().includes(s)     ||
      r.hotelId?.name?.toLowerCase().includes(s)
    );
  });

  // Stats rapides
  const totalRevenue = filtered
    .filter(r => ["confirmed", "completed"].includes(r.status))
    .reduce((s, r) => s + (r.totalPrice || 0), 0);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Réservations</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Toutes les réservations de votre portefeuille
          </p>
        </div>
        <button
          onClick={fetchData}
          className="w-9 h-9 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm hover:bg-gray-50 transition text-gray-400"
        >
          <TbRefresh size={16} />
        </button>
      </div>

      {/* ── STATS RAPIDES ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total",       value: filtered.length,                                                   color: "text-gray-900" },
          { label: "Confirmées",  value: filtered.filter(r => r.status === "confirmed").length,             color: "text-emerald-600" },
          { label: "En attente",  value: filtered.filter(r => r.status === "pending_cancel").length,        color: "text-orange-500" },
          { label: "CA filtré",   value: `${totalRevenue.toLocaleString("fr-FR")} €`,                      color: "text-[#3A9D23]" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-gray-400 mb-1">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* ── FILTRES ── */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex flex-wrap gap-3 items-center">
        <TbFilter size={15} className="text-gray-400 shrink-0" />

        {/* Recherche */}
        <div className="relative flex-1 min-w-[180px]">
          <TbSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Nom, email, bien..."
            value={filters.search}
            onChange={e => setFilter("search", e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
          />
        </div>

        {/* Statut */}
        <select
          value={filters.status}
          onChange={e => setFilter("status", e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] transition"
        >
          <option value="all">Tous les statuts</option>
          {Object.entries(STATUS_CONFIG).map(([key, c]) => (
            <option key={key} value={key}>{c.label}</option>
          ))}
        </select>

        {/* Bien */}
        <select
          value={filters.hotelId}
          onChange={e => setFilter("hotelId", e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] transition"
        >
          <option value="all">Tous les biens</option>
          {hotels.map(h => (
            <option key={h._id} value={h._id}>{h.name}</option>
          ))}
        </select>

        {/* Dates */}
        <input
          type="date"
          value={filters.from}
          onChange={e => setFilter("from", e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] transition"
        />
        <span className="text-xs text-gray-400">→</span>
        <input
          type="date"
          value={filters.to}
          onChange={e => setFilter("to", e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] transition"
        />

        {/* Reset */}
        {(filters.search || filters.status !== "all" || filters.hotelId !== "all" || filters.from || filters.to) && (
          <button
            onClick={() => setFilters({ search: "", status: "all", hotelId: "all", from: "", to: "" })}
            className="text-xs text-red-400 hover:text-red-600 transition"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* ── TABLE ── */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">

        {/* Header table */}
        <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 bg-gray-50">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide w-44 shrink-0">Voyageur</p>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide w-36 shrink-0">Bien</p>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide w-36 shrink-0">Séjour</p>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide w-28 shrink-0">Montant</p>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex-1">Statut</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-gray-400">Chargement...</p>
          </div>
        ) : error ? (
          <div className="flex items-center gap-3 p-6">
            <TbAlertCircle className="text-red-500" size={18} />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
              <TbCalendarCheck className="text-gray-400" size={22} />
            </div>
            <p className="text-sm text-gray-400">Aucune réservation trouvée</p>
          </div>
        ) : (
          filtered.map(r => (
            <ReservationRow
              key={r._id}
              r={r}
              expanded={expanded === r._id}
              onToggle={() => setExpanded(expanded === r._id ? null : r._id)}
            />
          ))
        )}
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-gray-400 text-center">
          {filtered.length} réservation{filtered.length > 1 ? "s" : ""} · CA : {totalRevenue.toLocaleString("fr-FR")} € · {toKMF(totalRevenue)} KMF
        </p>
      )}
    </div>
  );
}